import { useRef, useState } from "react";
import { Can } from "@core/permissions";
import { ApiError } from "@core/http";
import { Icon } from "@core/icons";
import { ImageWithPlaceholder } from "@core/media";
import { CursorTable, tableSpacing, type CursorTableColumn } from "@core/table";
import type {
  CatalogEntry,
  CatalogEntryInput,
  CatalogEntryUpdate,
  CatalogName,
} from "@core/types";
import { tokens } from "@core/theme/tokens";
import {
  Badge,
  EmptyState,
  ImpactConfirmDialog,
  inputStyle,
  ListLayout,
  RowIconAction,
  useToast,
} from "@portal/common";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import { CatalogEntryForm } from "./CatalogEntryForm";
import { CATALOG_PRESENTATION, MERCHANT_TYPES } from "./catalogPresentation";
import {
  useCatalogMutations,
  useCatalogs,
  useCityNames,
  useEntries,
} from "./catalogQueries";
import { catalogStyles } from "./catalogStyles";

function displayName(entry: CatalogEntry) {
  return typeof entry.name === "string" ? entry.name : entry.name.es;
}
function displayDescription(entry: CatalogEntry) {
  if (entry.description === null || entry.description === undefined) return "";
  return typeof entry.description === "string"
    ? entry.description
    : (entry.description.es ?? "");
}
function impactOf(error: ApiError) {
  return (
    (error.problem?.impact as
      | { uses?: number; entities?: string[]; affectedMembers?: number }
      | undefined) ?? {}
  );
}
const CATALOG_ERROR_MESSAGES: Partial<Record<string, string>> = {
  CODE_DUPLICATE: "Ya existe una entrada con ese código en este catálogo",
};
// PLAN-009 §3: CATALOG_IN_USE es un rechazo de negocio (no una validación de
// campo) — va al toast igual que cualquier otro error de servicio, con su
// texto armado desde `problem.impact` (no hay tabla estática posible para
// un mensaje con cifras). El resto pasa por `reportError`/
// `CATALOG_ERROR_MESSAGES`, que ya trae el identificador de correlación
// para un código sin mapear.
function reportCatalogError(
  toast: ReturnType<typeof useToast>,
  error: unknown,
) {
  if (error instanceof ApiError && error.code === "CATALOG_IN_USE") {
    const impact = impactOf(error);
    toast.show(
      `No se puede inactivar: ${impact.uses ?? 0} registros la están usando${impact.entities?.length ? ` en ${impact.entities.join(", ")}` : ""}.`,
      "error",
    );
    markErrorHandled(error);
    return;
  }
  reportError(toast, error, CATALOG_ERROR_MESSAGES);
}
function attributes(entry: CatalogEntry) {
  return (entry.attributes ?? {}) as Record<string, unknown>;
}
// Compartido por las composiciones especializadas de lista/tarjeta que
// solo editan/inactivan una entrada, sin arrastre ni otra acción propia
// (jscpd, quality-gate 2026-08-24 — la firma repetida contaba como clon).
type CatalogListProps = {
  entries: CatalogEntry[];
  onEdit: (entry: CatalogEntry) => void;
  onToggle: (entry: CatalogEntry) => void;
};
// Talleres y comercios resuelven cityId contra useCityNames() de la misma
// forma; extraído para no duplicar el bloque (jscpd, quality-gate 2026-08-24).
function cityLabel(
  cityNames: ReturnType<typeof useCityNames>,
  cityId: unknown,
) {
  if (!cityId) return "—";
  if (cityNames.isLoading) return "Cargando…";
  return cityNames.map.get(String(cityId)) ?? "—";
}
// Full CatalogEntryUpdate a partir de una entrada existente: lo necesita
// el reordenamiento por arrastre, que solo cambia sortOrder pero el
// contrato exige `name` en cada llamada de edición.
function toUpdateInput(
  entry: CatalogEntry,
  patch: Partial<CatalogEntryUpdate>,
): CatalogEntryUpdate {
  return {
    name: entry.name,
    description: entry.description,
    ...(entry.sortOrder === null ? {} : { sortOrder: entry.sortOrder }),
    attributes: entry.attributes,
    confirmed: false,
    ...patch,
  };
}

function RowActions({
  entry,
  onEdit,
  onToggle,
  column,
}: {
  entry: CatalogEntry;
  onEdit: (entry: CatalogEntry) => void;
  onToggle: (entry: CatalogEntry) => void;
  column?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: column ? "column" : "row",
        gap: tableSpacing.actionGap,
        flex: "none",
      }}
    >
      <Can module="catalogs" action="canEdit">
        <RowIconAction
          label="Editar"
          icon="edit"
          onClick={() => onEdit(entry)}
        />
      </Can>
      {entry.active ? (
        <Can module="catalogs" action="canInactivate">
          {entry.uses.total ? (
            <RowIconAction
              label="Ver bloqueo"
              icon="eye"
              onClick={() => onToggle(entry)}
            />
          ) : (
            <RowIconAction
              label="Inactivar"
              icon="deactivate"
              tone="danger"
              onClick={() => onToggle(entry)}
            />
          )}
        </Can>
      ) : null}
    </div>
  );
}

// referral-actions · admin.jsx:2922-2942 AchievementPreview. La pantalla es
// administración del catálogo, no el avance de un miembro real: se muestra
// la secuencia ordenada (número + nombre + color de cada acción activa) sin
// inventar un ratio de "completado" que no tiene dato real detrás — ver
// hallazgo documentado en run-step.md.
function AchievementPreview({ entries }: { entries: CatalogEntry[] }) {
  const active = entries.filter((entry) => entry.active);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        overflowX: "auto",
        padding: catalogStyles.referral.previewPadding,
      }}
    >
      {active.map((entry, index) => {
        const color = String(attributes(entry).color ?? tokens.text.tertiary);
        return (
          <div key={entry.id} style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: catalogStyles.referral.circleGap,
                flex: "none",
                width: catalogStyles.referral.circleItemWidth,
              }}
            >
              <div
                style={{
                  width: catalogStyles.referral.circleSize,
                  height: catalogStyles.referral.circleSize,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "none",
                  border: `1.5px solid ${color}`,
                  color,
                  font: `600 12px ${tokens.font.ui}`,
                }}
              >
                {entry.sortOrder ?? index + 1}
              </div>
              <span
                style={{
                  font: `600 ${catalogStyles.referral.nameFontSize}px ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                  textAlign: "center",
                  lineHeight: 1.3,
                }}
              >
                {displayName(entry)}
              </span>
            </div>
            {index < active.length - 1 ? (
              <div
                style={{
                  height: catalogStyles.referral.connectorHeight,
                  flex: `0 0 ${catalogStyles.referral.connectorWidth}px`,
                  marginBottom: catalogStyles.referral.connectorMarginBottom,
                  background: tokens.surface.line,
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// referral-actions · admin.jsx:2944-3059 CatAccionesReferido: filas
// arrastrables (no cards), sin librería de drag-and-drop (ADR-011) — eventos
// nativos draggable/onDragStart/onDragOver/onDrop, igual que el prototipo.
function ReferralActionsList({
  entries,
  onEdit,
  onToggle,
  onReorder,
}: {
  entries: CatalogEntry[];
  onEdit: (entry: CatalogEntry) => void;
  onToggle: (entry: CatalogEntry) => void;
  onReorder: (ordered: CatalogEntry[]) => void;
}) {
  const dragIndex = useRef<number | null>(null);
  const sorted = [...entries].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );
  const drop = (targetIndex: number) => {
    const source = dragIndex.current;
    dragIndex.current = null;
    if (source === null || source === targetIndex) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(source, 1);
    if (!moved) return;
    reordered.splice(targetIndex, 0, moved);
    onReorder(reordered);
  };
  return (
    <div>
      <div style={{ marginBottom: catalogStyles.header.marginBottom }}>
        <div
          className="label"
          style={{
            marginBottom: catalogStyles.referral.previewLabelMarginBottom,
          }}
        >
          Vista previa · secuencia de acciones activas
        </div>
        <div
          style={{
            border: `1px solid ${tokens.surface.line}`,
            borderRadius: 18,
            background: tokens.surface.bg2,
          }}
        >
          <AchievementPreview entries={sorted} />
        </div>
      </div>
      {sorted.map((entry, index) => {
        const a = attributes(entry);
        return (
          <div
            key={entry.id}
            data-testid="referral-action-row"
            draggable
            onDragStart={() => {
              dragIndex.current = index;
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => drop(index)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: catalogStyles.referral.row.gap,
              padding: catalogStyles.referral.row.padding,
              marginBottom: catalogStyles.referral.row.marginBottom,
              borderRadius: catalogStyles.referral.row.borderRadius,
              border: `1px solid ${tokens.surface.line}`,
              background: tokens.surface.surface,
              cursor: "grab",
              opacity: entry.active ? 1 : 0.55,
            }}
          >
            <Icon
              name="chevdown"
              size={15}
              color={tokens.text.tertiary}
              style={{ transform: "rotate(-90deg)", flex: "none" }}
            />
            <span
              style={{
                font: `600 ${catalogStyles.referral.row.orderFontSize}px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
                width: catalogStyles.referral.row.orderWidth,
                flex: "none",
              }}
            >
              {entry.sortOrder ?? index + 1}
            </span>
            <span
              style={{
                width: catalogStyles.referral.row.colorDotSize,
                height: catalogStyles.referral.row.colorDotSize,
                borderRadius: "50%",
                background: String(a.color ?? tokens.text.tertiary),
                flex: "none",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: catalogStyles.referral.row.titleGap,
                }}
              >
                <span
                  style={{
                    font: `600 ${catalogStyles.referral.row.titleFontSize}px ${tokens.font.ui}`,
                    color: tokens.text.primary,
                  }}
                >
                  {displayName(entry)}
                </span>
                {a.isFinal ? (
                  <Badge tone="achievement">Cuenta para el Gran Premio</Badge>
                ) : null}
              </div>
              <div
                style={{
                  font: `400 ${catalogStyles.referral.row.descFontSize}px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                  marginTop: catalogStyles.referral.row.descMarginTop,
                }}
              >
                {displayDescription(entry)}
              </div>
            </div>
            <Badge tone={entry.active ? "silver" : "neutral"}>
              {entry.active ? "Activa" : "Inactiva"}
            </Badge>
            <RowActions entry={entry} onEdit={onEdit} onToggle={onToggle} />
          </div>
        );
      })}
    </div>
  );
}

// car-models · admin.jsx:2802-2856 CatModelos. El contrato solo expone
// version + photoUrl (SPEC-011 §4): la "año · versión" del prototipo usa un
// campo de año que no existe en CatalogInputAttributes, así que no se
// fabrica — se muestra solo la versión (hallazgo documentado en run-step).
function CarModelsGrid({ entries, onEdit, onToggle }: CatalogListProps) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: catalogStyles.modelGrid.gridTemplateColumns,
        gap: catalogStyles.cards.gap,
      }}
    >
      {entries.map((entry) => {
        const a = attributes(entry);
        return (
          <article
            key={entry.id}
            data-testid="model-card"
            style={{
              overflow: "hidden",
              border: `1px solid ${tokens.surface.line}`,
              borderRadius: 18,
              background: tokens.surface.bg2,
              opacity: entry.active ? 1 : 0.55,
            }}
          >
            <ImageWithPlaceholder
              src={a.photoUrl ? String(a.photoUrl) : null}
              imageStyle={{
                width: "100%",
                height: catalogStyles.cards.modelImageHeight,
                objectFit: "cover",
                display: "block",
              }}
            />
            <div style={{ padding: catalogStyles.modelCard.padding }}>
              <div
                style={{
                  font: `600 ${catalogStyles.modelCard.nameFontSize}px ${tokens.font.ui}`,
                  color: tokens.text.primary,
                }}
              >
                {displayName(entry)}
              </div>
              <div
                style={{
                  font: `400 ${catalogStyles.modelCard.metaFontSize}px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                  margin: catalogStyles.modelCard.metaMargin,
                }}
              >
                Versión: {String(a.version ?? "—")}
              </div>
              <RowActions entry={entry} onEdit={onEdit} onToggle={onToggle} />
            </div>
          </article>
        );
      })}
    </div>
  );
}

// workshops · admin.jsx:2858-2917 CatTalleres: tabla de filas, no cards.
function WorkshopsTable({ entries, onEdit, onToggle }: CatalogListProps) {
  const cityNames = useCityNames();
  return (
    <div
      style={{
        border: `1px solid ${tokens.surface.line}`,
        borderRadius: 18,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: catalogStyles.workshopTable.gridTemplateColumns,
          padding: catalogStyles.table.headerPadding,
          background: tokens.surface.surface,
          color: tokens.text.tertiary,
          font: `600 ${catalogStyles.table.headerFontSize}px ${tokens.font.ui}`,
        }}
      >
        <span>NOMBRE</span>
        <span>CIUDAD</span>
        <span>DIRECCIÓN</span>
        <span>ESTADO</span>
        <span>ACCIONES</span>
      </div>
      {entries.map((entry, index) => {
        const a = attributes(entry);
        const city = cityLabel(cityNames, a.cityId);
        return (
          <div
            key={entry.id}
            data-testid="workshop-card"
            style={{
              display: "grid",
              gridTemplateColumns:
                catalogStyles.workshopTable.gridTemplateColumns,
              padding: catalogStyles.workshopTable.rowPadding,
              borderTop: index ? `1px solid ${tokens.surface.line}` : "none",
              alignItems: "center",
              opacity: entry.active ? 1 : 0.55,
            }}
          >
            <span
              style={{
                font: `600 ${catalogStyles.workshopTable.nameFontSize}px ${tokens.font.ui}`,
                color: tokens.text.primary,
              }}
            >
              {displayName(entry)}
            </span>
            <span
              style={{
                font: `400 ${catalogStyles.workshopTable.metaFontSize}px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
              }}
            >
              {city}
            </span>
            <span
              style={{
                font: `400 ${catalogStyles.workshopTable.metaFontSize}px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
              }}
            >
              {a.fromCrm
                ? `CRM · sincronizado ${String(a.syncedAt ?? "—")}`
                : String(a.address ?? "—")}
            </span>
            <Badge tone={entry.active ? "silver" : "neutral"}>
              {entry.active ? "Activa" : "Inactiva"}
            </Badge>
            <RowActions entry={entry} onEdit={onEdit} onToggle={onToggle} />
          </div>
        );
      })}
    </div>
  );
}

// merchants · admin.jsx:3687-3781 CatComercios: card horizontal con logo,
// datos y una fila de chips por validador (no un conteo agregado).
function MerchantsList({ entries, onEdit, onToggle }: CatalogListProps) {
  const cityNames = useCityNames();
  return (
    <div style={{ display: "grid", gap: catalogStyles.cards.gap }}>
      {entries.map((entry) => {
        const a = attributes(entry);
        const validators = Array.isArray(a.validators)
          ? (a.validators as Array<{
              id: string;
              name: string;
              active: boolean;
            }>)
          : [];
        const typeLabel =
          MERCHANT_TYPES.find(([value]) => value === a.type)?.[1] ??
          String(a.type ?? "—");
        const city = cityLabel(cityNames, a.cityId);
        return (
          <article
            key={entry.id}
            data-testid="merchant-card"
            style={{
              display: "flex",
              gap: catalogStyles.merchant.gap,
              padding: catalogStyles.cards.padding,
              border: `1px solid ${tokens.surface.line}`,
              borderRadius: 18,
              background: tokens.surface.bg2,
              opacity: entry.active ? 1 : 0.55,
            }}
          >
            <ImageWithPlaceholder
              src={a.logoUrl ? String(a.logoUrl) : null}
              iconSize={18}
              showText={false}
              imageStyle={{
                width: catalogStyles.cards.merchantLogoSize,
                height: catalogStyles.cards.merchantLogoSize,
                objectFit: "cover",
                borderRadius: tokens.radius.sm,
                flex: "none",
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: catalogStyles.merchant.headerGap,
                  marginBottom: catalogStyles.merchant.headerMarginBottom,
                }}
              >
                <span
                  style={{
                    font: `600 ${catalogStyles.merchant.nameFontSize}px ${tokens.font.ui}`,
                    color: tokens.text.primary,
                  }}
                >
                  {displayName(entry)}
                </span>
                <Badge tone={entry.active ? "silver" : "neutral"}>
                  {entry.active ? "Activa" : "Inactiva"}
                </Badge>
              </div>
              <div
                style={{
                  font: `400 ${catalogStyles.merchant.metaFontSize}px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                {typeLabel} · {city}
              </div>
              <div
                style={{
                  font: `400 ${catalogStyles.merchant.metaFontSize}px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                  marginTop: catalogStyles.merchant.metaMarginTop,
                }}
              >
                {String(a.address ?? "—")}
              </div>
              <div
                style={{
                  font: `400 ${catalogStyles.merchant.metaFontSize}px ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                  marginTop: catalogStyles.merchant.metaMarginTop,
                }}
              >
                {String(a.contact ?? "—")}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: catalogStyles.merchant.validatorsGap,
                  marginTop: catalogStyles.merchant.validatorsMarginTop,
                  flexWrap: "wrap",
                }}
              >
                <span className="label" style={{ marginRight: 2 }}>
                  Validadores
                </span>
                {validators.length ? (
                  validators.map((validator) => (
                    <span
                      key={validator.id}
                      style={{
                        padding: catalogStyles.badge.padding,
                        borderRadius: 999,
                        font: `600 ${catalogStyles.badge.fontSize}px ${tokens.font.ui}`,
                        background: validator.active
                          ? tokens.gold.dim
                          : tokens.surface.surface,
                        color: validator.active
                          ? tokens.gold.light
                          : tokens.text.tertiary,
                        border: `1px solid ${validator.active ? tokens.gold.line : tokens.surface.line}`,
                      }}
                    >
                      {validator.name}
                      {validator.active ? "" : " · inactivo"}
                    </span>
                  ))
                ) : (
                  <span
                    style={{
                      font: `400 ${catalogStyles.merchant.emptyValidatorsFontSize}px ${tokens.font.ui}`,
                      color: tokens.text.disabled,
                    }}
                  >
                    Sin usuarios validadores asignados
                  </span>
                )}
              </div>
            </div>
            <RowActions
              entry={entry}
              onEdit={onEdit}
              onToggle={onToggle}
              column
            />
          </article>
        );
      })}
    </div>
  );
}

function CatalogEntriesTable({ entries, onEdit, onToggle }: CatalogListProps) {
  const columns: CursorTableColumn<CatalogEntry>[] = [
    {
      key: "code",
      header: "CÓDIGO",
      width: "132px",
      render: (entry) => (
        <span
          style={{
            font: `600 ${catalogStyles.table.codeFontSize}px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
            letterSpacing: "0.03em",
          }}
        >
          {entry.code}
        </span>
      ),
    },
    {
      key: "name",
      header: "NOMBRE",
      width: "1.3fr",
      render: (entry) => (
        <span
          style={{
            font: `600 ${catalogStyles.table.nameFontSize}px ${tokens.font.ui}`,
          }}
        >
          {displayName(entry)}
          {entry.translationComplete === false ? (
            <small style={{ display: "block", color: tokens.danger.base }}>
              Inglés pendiente
            </small>
          ) : null}
        </span>
      ),
    },
    {
      key: "description",
      header: "DESCRIPCIÓN",
      width: "1.9fr",
      render: (entry) => (
        <span
          style={{
            color: tokens.text.tertiary,
            font: `400 ${catalogStyles.table.descriptionFontSize}px/1.5 ${tokens.font.ui}`,
          }}
        >
          {displayDescription(entry) || "—"}
        </span>
      ),
    },
    {
      key: "state",
      header: "ESTADO",
      width: "108px",
      render: (entry) => (
        <Badge tone={entry.active ? "silver" : "neutral"}>
          {entry.active ? "Activo" : "Inactivo"}
        </Badge>
      ),
    },
    {
      key: "uses",
      header: "USO",
      width: "168px",
      render: (entry) => (
        <span
          style={{
            color: tokens.text.tertiary,
            font: `400 ${catalogStyles.table.useFontSize}px ${tokens.font.ui}`,
          }}
        >
          usado en {entry.uses.total} registro
          {entry.uses.total === 1 ? "" : "s"}
        </span>
      ),
    },
  ];
  return (
    <CursorTable
      columns={columns}
      rows={entries}
      rowKey={(entry) => entry.id}
      page={{ totalCount: entries.length, nextCursor: null }}
      onLoadMore={() => undefined}
      rowActions={(entry) => (
        <RowActions entry={entry} onEdit={onEdit} onToggle={onToggle} />
      )}
      actionWidth="168px"
      columnGap={catalogStyles.table.gap}
      headerPadding={catalogStyles.table.headerPadding}
      rowPadding={catalogStyles.table.rowPadding}
      headerFont={`600 ${catalogStyles.table.headerFontSize}px ${tokens.font.ui}`}
      minWidth={1080}
      showFooter={false}
    />
  );
}

export function CatalogsScreen() {
  const toast = useToast();
  const [catalog, setCatalog] = useState<CatalogName>("member-actions");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editing, setEditing] = useState<CatalogEntry | null | undefined>(
    undefined,
  );
  const [finalConfirm, setFinalConfirm] = useState<{
    id: string;
    input: CatalogEntryUpdate;
    affectedMembers: number;
  } | null>(null);
  const [inactivate, setInactivate] = useState<CatalogEntry | null>(null);
  const [inUseNotice, setInUseNotice] = useState<string | null>(null);
  // Caso real (trace f4575316-5c75-4c27-83d0-340db0aafd9f): el 400 de
  // creación/edición se lo pasamos a `CatalogEntryForm` para que intente
  // asociarlo a un campo (`applyApiFieldErrors`); si no puede, nos avisa por
  // `onUnhandledSubmitError` y ahí sí sale el toast de siempre.
  const [submitError, setSubmitError] = useState<unknown>(undefined);
  const catalogs = useCatalogs();
  const entries = useEntries(catalog, includeInactive, search);
  const mutations = useCatalogMutations(catalog);
  const summary = catalogs.data?.find((item) => item.catalog === catalog);
  // PLAN-009 §3: el error de servicio (código sin mapear a un campo) va
  // siempre al toast — `CONFIRM_FINAL_ACTION` es la única excepción: abre el
  // diálogo de confirmación (aviso), no pinta ni un toast de error.
  // El toast genérico solo sale cuando `CatalogEntryForm` avisa (por
  // `onUnhandledSubmitError`) que no pudo asociar el rechazo a ningún campo
  // propio — ver el comentario de `submitError` más arriba.
  const handleUnhandledSubmitError = (error: unknown) =>
    reportCatalogError(toast, error);
  const save = (input: CatalogEntryInput | CatalogEntryUpdate) => {
    if (!editing) {
      mutations.create.mutate(input as CatalogEntryInput, {
        onSuccess: () => {
          setEditing(undefined);
          toast.show("Entrada creada correctamente.");
        },
        onError: (error) => setSubmitError(error),
      });
      return;
    }
    mutations.edit.mutate(
      { id: editing.id, input: input as CatalogEntryUpdate },
      {
        onSuccess: () => {
          setEditing(undefined);
          toast.show("Cambios guardados correctamente.");
        },
        onError: (error) => {
          if (
            error instanceof ApiError &&
            error.code === "CONFIRM_FINAL_ACTION"
          ) {
            setFinalConfirm({
              id: editing.id,
              input: input as CatalogEntryUpdate,
              affectedMembers: impactOf(error).affectedMembers ?? 0,
            });
            markErrorHandled(error);
            return;
          }
          setSubmitError(error);
        },
      },
    );
  };
  const confirmFinal = () => {
    if (!finalConfirm) return;
    mutations.edit.mutate(
      {
        id: finalConfirm.id,
        input: { ...finalConfirm.input, confirmed: true },
      },
      {
        onSuccess: () => {
          setFinalConfirm(null);
          setEditing(undefined);
          toast.show("Acción final actualizada correctamente.");
        },
        onError: (error) => reportCatalogError(toast, error),
      },
    );
  };
  const startEdit = (entry: CatalogEntry) => {
    setEditing(entry);
    setInUseNotice(null);
  };
  const selected = catalogs.data?.find((item) => item.catalog === catalog);
  const requestInactivation = (entry: CatalogEntry) => {
    setInUseNotice(null);
    if (entry.uses.total > 0) {
      setInUseNotice(
        `No se puede inactivar: ${entry.uses.total} registros la están usando${entry.uses.byEntity.length ? ` en ${entry.uses.byEntity.map((item) => item.entity).join(", ")}` : ""}.`,
      );
      return;
    }
    setInactivate(entry);
  };
  const confirmInactivation = () => {
    if (!inactivate) return;
    mutations.inactivate.mutate(inactivate.id, {
      onSuccess: () => {
        setInactivate(null);
        toast.show("Entrada inactivada correctamente.");
      },
      // CATALOG_IN_USE cierra el diálogo (ya no aplica reintentar sin
      // cambiar nada); cualquier otro error de servicio se queda abierto
      // para reintentar, como ya hacía antes de mover el aviso al toast.
      onError: (error) => {
        if (error instanceof ApiError && error.code === "CATALOG_IN_USE") {
          setInactivate(null);
        }
        reportCatalogError(toast, error);
      },
    });
  };
  // referral-actions: no hay endpoint de reordenamiento masivo — una llamada
  // a mutations.edit por cada entrada cuyo sortOrder cambió, igual que el
  // resto de la pantalla (patrón "una edición por entrada"). Cada mutate va
  // en vuelo en paralelo (code-reviewer, 2026-08-24): si una falla, el
  // servidor terminó con un orden distinto al que ve la fila local, así que
  // se avisa por entrada en vez de dejar la UI mintiendo sobre el resultado.
  //
  // T2 (qa-bug-matrix-2026-09.md §5.5, #19): guardar sin error no acusaba
  // nada. Un toast por entrada sería la avalancha que la cola existe para
  // evitar, así que se agrega un único acuse cuando TODAS las mutaciones en
  // vuelo terminan bien; si alguna falla, el banner por entrada ya existente
  // sigue siendo la única señal (y `markErrorHandled` evita que el fallback
  // global sume un toast genérico por cada una).
  const reorderReferral = (ordered: CatalogEntry[]) => {
    const changes = ordered
      .map((entry, index) => ({ entry, nextOrder: index + 1 }))
      .filter(({ entry, nextOrder }) => entry.sortOrder !== nextOrder);
    if (changes.length === 0) return;
    let remaining = changes.length;
    let hadError = false;
    changes.forEach(({ entry, nextOrder }) => {
      mutations.edit.mutate(
        {
          id: entry.id,
          input: toUpdateInput(entry, { sortOrder: nextOrder }),
        },
        {
          onError: (error) => {
            hadError = true;
            toast.show(
              `No se pudo guardar el nuevo orden de «${displayName(entry)}». Actualiza la página para ver el orden real del servidor.`,
              "error",
            );
            markErrorHandled(error);
          },
          onSettled: () => {
            remaining -= 1;
            if (remaining === 0 && !hadError) {
              toast.show("Nuevo orden de acciones de referido guardado.");
            }
          },
        },
      );
    });
  };
  return (
    <div>
      <header style={{ marginBottom: 26 }}>
        <h1
          style={{
            margin: 0,
            font: `300 34px ${tokens.font.display}`,
            color: tokens.text.primary,
          }}
        >
          Catálogos
        </h1>
        <p
          style={{
            margin: "6px 0 0",
            font: `400 13px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          Listas de valores usadas en formularios y filtros del portal.
        </p>
      </header>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: catalogStyles.master.gridTemplateColumns,
          gap: catalogStyles.master.gap,
          alignItems: "start",
        }}
      >
        <aside
          style={{
            padding: catalogStyles.sidebar.padding,
            border: `1px solid ${tokens.surface.line}`,
            borderRadius: 18,
            background: tokens.surface.bg2,
          }}
        >
          <div
            className="label"
            style={{ padding: catalogStyles.sidebar.labelPadding }}
          >
            CATÁLOGOS
          </div>
          {CATALOG_PRESENTATION.map((item) => (
            <button
              key={item.catalog}
              type="button"
              onClick={() => {
                setCatalog(item.catalog);
                setEditing(undefined);
                setInUseNotice(null);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                padding: catalogStyles.sidebar.itemPadding,
                borderRadius: tokens.radius.sm,
                marginBottom: catalogStyles.sidebar.itemMarginBottom,
                font: `600 ${catalogStyles.sidebar.itemFontSize}px ${tokens.font.ui}`,
                background:
                  catalog === item.catalog
                    ? tokens.surface.surface2
                    : "transparent",
                border: `1px solid ${catalog === item.catalog ? tokens.surface.line : "transparent"}`,
                color:
                  catalog === item.catalog
                    ? tokens.text.primary
                    : tokens.text.tertiary,
              }}
            >
              {item.label}
            </button>
          ))}
        </aside>
        <section>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              gap: catalogStyles.subheader.gap,
            }}
          >
            <div>
              <h2
                className="display"
                style={{
                  margin: "0 0 16px",
                  fontSize: 23,
                  color: tokens.text.primary,
                }}
              >
                {selected?.name ?? "Catálogos"}
              </h2>
            </div>
            <Can module="catalogs" action="canCreate">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setEditing(null);
                  setInUseNotice(null);
                }}
              >
                Nueva entrada
              </button>
            </Can>
          </div>
          {editing !== undefined ? (
            // Revisión en vivo 2026-09-22, punto #4A: el `Modal` ya no se
            // arma acá para envolver al formulario — lo abre el propio
            // `CatalogEntryForm` con la prop `modal` de `FormLayout`, igual
            // que `MemberForm`/`UserForm`/`ProfileForm`. Así el título y los
            // botones quedan en las regiones fijas y solo desplaza el cuerpo.
            <CatalogEntryForm
              catalog={catalog}
              bilingual={Boolean(summary?.bilingual)}
              entry={editing ?? undefined}
              modal={{
                label: editing ? "Editar entrada" : "Nueva entrada",
                width:
                  catalog === "workshops" || catalog === "merchants"
                    ? catalogStyles.modalWidth.workshop
                    : catalog === "car-models"
                      ? catalogStyles.modalWidth.model
                      : catalogStyles.modalWidth.generic,
                onClose: () => setEditing(undefined),
              }}
              onSubmit={save}
              onCancel={() => setEditing(undefined)}
              submitting={
                mutations.create.isPending || mutations.edit.isPending
              }
              submitError={submitError}
              onUnhandledSubmitError={handleUnhandledSubmitError}
            />
          ) : (
            <ListLayout
              search={
                <>
                  <input
                    aria-label="Buscar"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar"
                    style={{ ...inputStyle, width: catalogStyles.search.width }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      font: `500 ${catalogStyles.checkboxLabelFontSize}px ${tokens.font.ui}`,
                      color: tokens.text.secondary,
                    }}
                  >
                    <select
                      aria-label="Filtrar por estado"
                      value={includeInactive ? "all" : "active"}
                      onChange={(event) =>
                        setIncludeInactive(event.target.value === "all")
                      }
                      style={{
                        ...inputStyle,
                        width: "auto",
                        minWidth: 140,
                        cursor: "pointer",
                      }}
                    >
                      <option value="all">Estado · todos</option>
                      <option value="active">Estado · activos</option>
                    </select>
                  </div>
                </>
              }
            >
              {inUseNotice ? (
                <p
                  role="alert"
                  style={{
                    color: tokens.danger.base,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: catalogStyles.merchant.headerGap,
                  }}
                >
                  <span>{inUseNotice}</span>
                  <button
                    type="button"
                    className="chip"
                    aria-label="Cerrar aviso"
                    onClick={() => setInUseNotice(null)}
                  >
                    ×
                  </button>
                </p>
              ) : null}
              {entries.isLoading ? (
                <div style={{ padding: catalogStyles.loadingPadding }}>
                  Cargando entradas…
                </div>
              ) : entries.isError ? (
                <EmptyState
                  kind="error"
                  action={
                    <button
                      type="button"
                      className="btn"
                      onClick={() => entries.refetch()}
                    >
                      Recargar
                    </button>
                  }
                />
              ) : entries.data?.length ? (
                catalog === "referral-actions" ? (
                  <ReferralActionsList
                    entries={entries.data}
                    onEdit={startEdit}
                    onToggle={requestInactivation}
                    onReorder={reorderReferral}
                  />
                ) : catalog === "car-models" ? (
                  <CarModelsGrid
                    entries={entries.data}
                    onEdit={startEdit}
                    onToggle={requestInactivation}
                  />
                ) : catalog === "workshops" ? (
                  <WorkshopsTable
                    entries={entries.data}
                    onEdit={startEdit}
                    onToggle={requestInactivation}
                  />
                ) : catalog === "merchants" ? (
                  <MerchantsList
                    entries={entries.data}
                    onEdit={startEdit}
                    onToggle={requestInactivation}
                  />
                ) : (
                  <CatalogEntriesTable
                    entries={entries.data}
                    onEdit={startEdit}
                    onToggle={requestInactivation}
                  />
                )
              ) : (
                <EmptyState kind={search ? "no-results" : "no-data"} />
              )}
            </ListLayout>
          )}
        </section>
        <ImpactConfirmDialog
          open={Boolean(inactivate)}
          eyebrow={selected ? `Catálogos · ${selected.name}` : "Catálogos"}
          title={`¿Inactivar «${inactivate ? displayName(inactivate) : ""}»?`}
          affectedCount={inactivate?.uses.total ?? 0}
          affectedLabel="La entrada dejará de aparecer en nuevos selectores."
          onCancel={() => setInactivate(null)}
          onConfirm={confirmInactivation}
          confirmLabel="Sí, inactivar"
        />
        <ImpactConfirmDialog
          open={Boolean(finalConfirm)}
          eyebrow={selected ? `Catálogos · ${selected.name}` : "Catálogos"}
          title="¿Cambiar la acción final?"
          affectedCount={finalConfirm?.affectedMembers ?? 0}
          affectedLabel="miembros verán afectado su avance del Gran Premio."
          onCancel={() => setFinalConfirm(null)}
          onConfirm={confirmFinal}
          confirmLabel="Confirmar cambio"
          danger={false}
        />
      </div>
    </div>
  );
}
